import { ChangeDetectionStrategy, Component, effect, inject, signal } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { FonctionsService } from 'src/app/services/fonctions-partagees.service';
import { HttpService } from 'src/app/services/http.service';
import { UtilisateurService } from 'src/app/services/utilisateur.service';
import { EquipmentsModaleComponent } from '../equipments-modale/equipments-modale.component';
import { Equipment } from 'src/app/bo/equipment';
import { environment } from 'src/environments/environment';
import { EquipmentType } from 'src/app/bo/equipmentType';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from '@libs/front/components/button/button.component';
import { DataTableComponent, TableColumn } from '@libs/front/components/data-table/data-table.component';
import { CustomAffichage, ProprieteAffichee, TypePropriete } from 'src/app/bo/proprieteAffichee';

export enum TypeEquipment {
  STATIC,
  MOBILE
}

@Component({
  selector: 'app-equipments',
  templateUrl: './equipments.component.html',
  styleUrls: ['./equipments.component.scss'],
  standalone: true,
  imports: [ButtonComponent, DataTableComponent, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EquipmentsComponent {
  private readonly modalService = inject(BsModalService);
  private readonly http = inject(HttpService);
  private readonly fonctionsService = inject(FonctionsService);
  protected readonly utilService = inject(UtilisateurService);

  protected readonly tabEquipments = signal<Equipment[]>([]);
  protected readonly equipmentTypes = signal<EquipmentType[]>([]);
  protected readonly erreurRecuperation = signal(false);

  readonly tableColumns: TableColumn[] = [
    { key: 'name',                   label: 'GLOBAL.NOM' },
    { key: 'model',                  label: 'GLOBAL.MODEL' },
    { key: 'type',                   label: 'GLOBAL.TYPE' },
    { key: 'withExternalConnection', label: 'GLOBAL.WITH_EXTERNAL_CONNECTION' },
  ];
  
  protected readonly isSync = signal(false);

  private modale: BsModalRef;

  protected readonly proprietesAffichees: ProprieteAffichee[] = [
    new ProprieteAffichee('name', 'GLOBAL.NOM', ''),
    new ProprieteAffichee('model', 'GLOBAL.MODEL', ''),
    new ProprieteAffichee('type', 'GLOBAL.TYPE', ''),
    new ProprieteAffichee('withExternalConnection', 'GLOBAL.WITH_EXTERNAL_CONNECTION', '', new CustomAffichage(TypePropriete.Boolean, 'fas fa-check-circle checkbox-epc-green', 'fas fa-minus-circle checkbox-epc-red')),
  ];

 

  constructor() {
    effect(() => {
      this.utilService.currentSite();
      this.refreshData();
    });
  }

  private refreshData(): void {
    this.erreurRecuperation.set(false);
    this.tabEquipments.set([]);
    this.equipmentTypes.set([]);
    this.http.get<EquipmentType[]>('Config/equipment-type').subscribe({
      next: (types) => {
        this.equipmentTypes.set(types);
        this.http.get<Equipment[]>('Sites/' + this.utilService.currentSite().siteId + '/Equipment').subscribe({
          next: (resultat) => {
            this.tabEquipments.set(this.mapEquipmentList(resultat, types));
          },
          error: () => {
            this.fonctionsService.launch_toast(false);
          }
        });
      },
      error: () => {
        this.erreurRecuperation.set(true);
      }
    });
  }

  private mapEquipmentList(equipments: Equipment[], types: EquipmentType[]): Equipment[] {
    return equipments.map(obj => {
      const typeObj = types.find(t => t.typeId === obj.typeId);
      const typeSuffix = (obj.externalId != null) ? this.enumToString(TypeEquipment.MOBILE) : this.enumToString(TypeEquipment.STATIC);
      const typeLibelle = typeObj
        ? `${typeObj.name} (${typeSuffix})`
        : typeSuffix;
      return {
        ...obj,
        withExternalConnection: obj.externalId != null,
        type: typeLibelle
      };
    });
  }

  protected ouvrirModale(eq: Equipment): void {
    this.fonctionsService.loading_screen(true);
    const initialState = {
      equipment: { ...eq },
      estModif: true,
      siteId: this.utilService.currentSite().siteId,
      equipmentTypes: this.equipmentTypes()
    };
    this.fonctionsService.loading_screen(false);
    this.modale = this.modalService.show(EquipmentsModaleComponent, { backdrop: true, ignoreBackdropClick: true, keyboard: false, initialState });
    this.modale.content.dataEmitter.subscribe((data: Equipment) => {
      const current = this.tabEquipments();
      const indexToUpdate = current.findIndex(x => x.equipementId == data.equipementId);
      if (indexToUpdate !== -1) {
        const updated = [...current];
        updated[indexToUpdate] = { ...updated[indexToUpdate], ...data };
        this.tabEquipments.set(this.mapEquipmentList(updated, this.equipmentTypes()));
      }
    });
  }

  protected ouvrirModaleAjout(): void {
    const initialState = {
      equipment: {},
      estModif: false,
      siteId: this.utilService.currentSite().siteId,
      equipmentTypes: this.equipmentTypes()
    };
    this.modale = this.modalService.show(EquipmentsModaleComponent, { backdrop: true, ignoreBackdropClick: true, keyboard: false, initialState });
    this.modale.content.dataEmitter.subscribe((data: Equipment) => {
      const updated = [...this.tabEquipments(), data];
      this.tabEquipments.set(this.mapEquipmentList(updated, this.equipmentTypes()));
    });
  }

  protected syncEquipments(): void {
    const today = new Date();
    const minDate = new Date(today);
    minDate.setDate(today.getDate() - environment.hibooRefreshRateInDays);

    const formatDate = (date: Date) => date.toISOString().split('T')[0];

    const siteId = this.utilService.currentSite().siteId;
    const url = `Hiboo/${siteId}/SyncEquipments?minDate=${formatDate(minDate)}&maxDate=${formatDate(today)}`;

    this.http.get(url, null).subscribe({
      next: (result) => {
        if (result) {
          this.fonctionsService.launch_toast(true);
          this.isSync.set(true);
          this.refreshData();
        }
      },
      error: () => {
        this.fonctionsService.launch_toast(false);
      }
    });
  }

  protected enumToString(enumValue: TypeEquipment): string {
    return TypeEquipment[enumValue];
  }
}
